import type { RedoOnboardingV2RpcClient } from "@redotech/redo-onboarding-api/client";
import { useEffect, useState } from "react";

export interface MerchantInfo {
  merchantId: string;
  storeName: string;
  storeUrl: string;
  email: string;
  logoUrl?: string;
  /** Public storefront domain (custom domain if configured, otherwise myshopify domain) */
  domain?: string;
  /** Internal myshopify.com domain, always set after install */
  myshopifyDomain?: string;
  /** Shopify admin dashboard URL (e.g. https://admin.shopify.com/store/{shop}) */
  shopifyAdminUrl?: string;
  /** Deep link URL that opens the Shopify theme editor's app embeds drawer */
  shopifyAppEmbedEditorUrl?: string;
  /** Deep link URL that opens the Shopify checkout editor on the thank you page */
  shopifyCheckoutEditorUrl?: string;
  /** Whether the Redo app embed is currently enabled in the merchant's theme */
  appEmbedEnabled: boolean;
}

export interface OnboardingFeatureFlags {
  /** When false, OMS onboarding shows the Calendly schedule-call card instead of the self-serve flow. */
  omsOnboarding: boolean;
  /** When false, Contextual onboarding shows the Calendly schedule-call card instead of the self-serve flow. */
  contextualOnboarding: boolean;
}

interface InitializeResult {
  merchantInfo: MerchantInfo;
  enabledProducts: Record<string, boolean>;
  featureFlags: OnboardingFeatureFlags;
  preferredLocale?: string;
  shopperLocalizationEnabled: boolean;
}

let sharedPromise: Promise<InitializeResult> | null = null;
let sharedResult: InitializeResult | null = null;
let sharedError: Error | null = null;
const listeners = new Set<() => void>();

function notifyListeners() {
  for (const fn of listeners) {
    fn();
  }
}

/** Kick off the initialize fetch immediately. Call once at app startup. */
export function prefetchMerchantInfo(
  rpcClient: RedoOnboardingV2RpcClient | undefined,
) {
  if (!rpcClient || sharedPromise) {
    return;
  }
  sharedPromise = rpcClient
    .initialize({})
    .then((data) => {
      sharedResult = data;
      notifyListeners();
      return sharedResult;
    })
    .catch((err) => {
      sharedError = err instanceof Error ? err : new Error(String(err));
      notifyListeners();
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      return null as unknown as InitializeResult;
    });
  notifyListeners();
}

/** Read merchant info from the prefetched cache. */
export function useMerchantInfo() {
  const [result, setResult] = useState<InitializeResult | null>(sharedResult);
  const [loading, setLoading] = useState(!sharedResult && !!sharedPromise);
  const [error, setError] = useState<Error | null>(sharedError);

  useEffect(() => {
    function sync() {
      if (sharedResult) {
        setResult(sharedResult);
        setLoading(false);
        setError(null);
      } else if (sharedError) {
        setError(sharedError);
        setLoading(false);
      } else if (sharedPromise) {
        setLoading(true);
      } else {
        setLoading(false);
      }
    }

    sync();

    listeners.add(sync);
    return () => {
      listeners.delete(sync);
    };
  }, []);

  const merchantInfo = result?.merchantInfo ?? null;
  const enabledProducts = result?.enabledProducts ?? null;
  const featureFlags = result?.featureFlags ?? null;
  const preferredLocale = result?.preferredLocale;
  const shopperLocalizationEnabled =
    result?.shopperLocalizationEnabled ?? false;

  const shopifyThemesUrl =
    merchantInfo?.shopifyAppEmbedEditorUrl ??
    (merchantInfo?.shopifyAdminUrl
      ? `${merchantInfo.shopifyAdminUrl}/themes`
      : null);

  const shopifyCheckoutEditorUrl =
    merchantInfo?.shopifyCheckoutEditorUrl ??
    (merchantInfo?.shopifyAdminUrl
      ? `${merchantInfo.shopifyAdminUrl}/settings/checkout/editor`
      : null);

  return {
    merchantInfo,
    enabledProducts,
    featureFlags,
    preferredLocale,
    shopperLocalizationEnabled,
    shopifyThemesUrl,
    shopifyCheckoutEditorUrl,
    loading,
    error,
  };
}
